import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CardMedia,
  Button,
  Grid2,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { itemTypeConfig, SUPPLIER_ITEM_TYPES } from "../../Containers/Supplier/FindSupplier/itemTypes";

// Chips for the OTHER catalogs this supplier sells in. A manager browsing
// stores shouldn't miss that the same business also does services — tabs
// separate the search, not the supplier.
const otherTypesChip = {
  display: "inline-flex",
  alignItems: "center",
  height: "18px",
  padding: "0 8px",
  borderRadius: "12px",
  fontSize: "11px",
  fontWeight: 500,
  color: "rgba(62, 63, 76, 0.8)",
  backgroundColor: "rgba(234, 234, 234, 0.6)",
};

const SupplierCard = ({ supplier, itemType, displaySupplierProfile, buttonText }) => {
  const { t } = useTranslation([
    'page-find-new-suppliers',
    'categories',
    'products_categories',
    'restaurants_categories',
  ]);
  // Categories belong to the catalog being browsed, so they are read from the
  // tree of the open tab.
  const categoriesNamespace = itemTypeConfig(itemType).categoriesNamespace;
  const otherTypes = (supplier.item_types || []).filter(
    (type) => type !== itemType && SUPPLIER_ITEM_TYPES.some((t) => t.key === type),
  );
  return (
    <Card sx={{ display: "flex", flexDirection: "column", width: "100%", boxShadow: "none !important", borderRadius: "12px", bgcolor: "#fcfcfc", borderColor: "#EAEAEA", borderWidth: "1px", borderStyle: "solid", paddingTop: "24px" }}>
      {/* Image Section */}
      <CardMedia
        component="img"
        image={supplier.display_image_url}
        alt={supplier.full_name}
        sx={{
          height: "120px",
          width: "120px",
          margin: "0 auto",
          marginBottom: "-40px",
          zIndex: 1,
          borderRadius: "50%",
        }}
      />

      {/* Content Section */}
      <CardContent sx={{ display: "flex", flexDirection: "column", flexGrow: 1, justifyContent: "space-between", borderTop: "1px solid #eaeaea", padding: "24px", paddingTop: "60px", "--Paper-shadow": "none", bgcolor: "#ffffff", boxShadow: "none !important" }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ mb: "6px", color: "#2F2B3D", fontSize: '19px', fontWeight: 500, textAlign: "center", lineHeight: "24px" }}>
            {supplier.full_name}
          </Typography>

          <Typography variant="body2" sx={{ mb: 3, color: "#2F2B3D", fontSize: '15px', fontWeight: 400, textAlign: "center", lineHeight: "20px" }}>
            {supplier.business_nickname}
          </Typography>

          <Grid2 container spacing={2} direction="column">

            {/* Location */}
            <Grid2 xs={12} display="flex" alignItems="flex-start" gap={"12px"} marginBottom={"12px"}>
              <svg style={{ minWidth: "20px" }} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M10 1.66666C11.9891 1.66666 13.8968 2.45683 15.3033 3.86336C16.7098 5.26988 17.5 7.17753 17.5 9.16666C17.5 11.7283 16.1033 13.825 14.6317 15.3292C13.8964 16.0725 13.0941 16.7465 12.235 17.3425L11.88 17.5842L11.7133 17.695L11.3992 17.895L11.1192 18.0658L10.7725 18.2675C10.5372 18.4018 10.2709 18.4724 10 18.4724C9.72906 18.4724 9.4628 18.4018 9.2275 18.2675L8.88083 18.0658L8.4475 17.7992L8.2875 17.695L7.94583 17.4675C7.01903 16.8404 6.15575 16.1242 5.36833 15.3292C3.89667 13.8242 2.5 11.7283 2.5 9.16666C2.5 7.17753 3.29018 5.26988 4.6967 3.86336C6.10322 2.45683 8.01088 1.66666 10 1.66666ZM10 3.33332C8.4529 3.33332 6.96917 3.9479 5.87521 5.04187C4.78125 6.13583 4.16667 7.61956 4.16667 9.16666C4.16667 11.1017 5.22667 12.8 6.55917 14.1633C7.13211 14.7433 7.75138 15.2756 8.41083 15.755L8.7925 16.0267C8.91583 16.1128 9.03444 16.1928 9.14833 16.2667L9.47333 16.475L9.75917 16.6492L10 16.79L10.3792 16.5658L10.685 16.3742C10.8478 16.2708 11.0219 16.155 11.2075 16.0267L11.5892 15.755C12.2486 15.2756 12.8679 14.7433 13.4408 14.1633C14.7733 12.8008 15.8333 11.1017 15.8333 9.16666C15.8333 7.61956 15.2188 6.13583 14.1248 5.04187C13.0308 3.9479 11.5471 3.33332 10 3.33332ZM10 5.83332C10.8841 5.83332 11.7319 6.18451 12.357 6.80963C12.9821 7.43476 13.3333 8.2826 13.3333 9.16666C13.3333 10.0507 12.9821 10.8986 12.357 11.5237C11.7319 12.1488 10.8841 12.5 10 12.5C9.11594 12.5 8.2681 12.1488 7.64298 11.5237C7.01786 10.8986 6.66667 10.0507 6.66667 9.16666C6.66667 8.2826 7.01786 7.43476 7.64298 6.80963C8.2681 6.18451 9.11594 5.83332 10 5.83332ZM10 7.49999C9.55797 7.49999 9.13405 7.67559 8.82149 7.98815C8.50893 8.30071 8.33333 8.72463 8.33333 9.16666C8.33333 9.60868 8.50893 10.0326 8.82149 10.3452C9.13405 10.6577 9.55797 10.8333 10 10.8333C10.442 10.8333 10.866 10.6577 11.1785 10.3452C11.4911 10.0326 11.6667 9.60868 11.6667 9.16666C11.6667 8.72463 11.4911 8.30071 11.1785 7.98815C10.866 7.67559 10.442 7.49999 10 7.49999Z" fill="#2F2B3D" />
              </svg>
              <div>
                <Typography variant="subtitle1" sx={{ color: "rgba(47, 43, 61, 0.7)", fontSize: '15px', fontWeight: 400, lineHeight: "15px", textTransform: 'uppercase', paddingBottom: "10px" }}>
                 {t('supplier_card.business_address')}
                </Typography>
                <Typography variant="body2" sx={{ color: "#2F2B3D", fontSize: '15px', fontWeight: 400, lineHeight: "20px" }}>
                  {supplier.location ? supplier.location : "-"}
                </Typography>
              </div>
            </Grid2>
            {/* Operational Zones 
          <Grid2 xs={12} display="flex" alignItems="flex-start" gap={"12px"} marginBottom={"12px"}>
          <svg width="20" height="20" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="5.57422" cy="4" r="3" stroke="#3E3F4C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10.5 1.14557C11.7038 1.53572 12.5742 2.66621 12.5742 4C12.5742 5.33379 11.7038 6.46428 10.5 6.85443" stroke="#3E3F4C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M1 13.9995V13.1245C1 11.1915 2.56698 9.62451 4.49996 9.62451H6.37495" stroke="#3E3F4C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10.375 13.9995V13.1245C10.375 11.1915 8.80802 9.62451 6.87504 9.62451H5.00005" stroke="#3E3F4C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14.375 13.9995V13.1245C14.375 11.1915 13 9.62451 11.875 9.62451" stroke="#3E3F4C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
            <div>
              <Typography variant="subtitle1" sx={{ color:"rgba(47, 43, 61, 0.7)", fontSize: '15px', fontWeight: 400, lineHeight:"15px", textTransform: 'uppercase', paddingBottom: "10px" }}>
                Operational Zones
              </Typography>
              <Typography variant="body2" sx={{ color:"#2F2B3D", fontSize: '15px', fontWeight: 400, lineHeight:"20px" }}>
                 {supplier.operational_zones.map(operational_zone=> operational_zone.name).join(' , ')}
              </Typography>
            </div>
          </Grid2>*/}
            {/* Categories */}
            <Grid2 item xs={12} display="flex" alignItems="flex-start" gap={"12px"}>
              <svg style={{ minWidth: "20px" }} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.33333 2.5H3.33333C3.11232 2.5 2.90036 2.5878 2.74408 2.74408C2.5878 2.90036 2.5 3.11232 2.5 3.33333V8.33333C2.5 8.55435 2.5878 8.76631 2.74408 8.92259C2.90036 9.07887 3.11232 9.16667 3.33333 9.16667H8.33333C8.55435 9.16667 8.76631 9.07887 8.92259 8.92259C9.07887 8.76631 9.16667 8.55435 9.16667 8.33333V3.33333C9.16667 3.11232 9.07887 2.90036 8.92259 2.74408C8.76631 2.5878 8.55435 2.5 8.33333 2.5ZM7.5 7.5H4.16667V4.16667H7.5V7.5ZM16.6667 10.8333H11.6667C11.4457 10.8333 11.2337 10.9211 11.0774 11.0774C10.9211 11.2337 10.8333 11.4457 10.8333 11.6667V16.6667C10.8333 16.8877 10.9211 17.0996 11.0774 17.2559C11.2337 17.4122 11.4457 17.5 11.6667 17.5H16.6667C16.8877 17.5 17.0996 17.4122 17.2559 17.2559C17.4122 17.0996 17.5 16.8877 17.5 16.6667V11.6667C17.5 11.4457 17.4122 11.2337 17.2559 11.0774C17.0996 10.9211 16.8877 10.8333 16.6667 10.8333ZM15.8333 15.8333H12.5V12.5H15.8333V15.8333ZM14.1667 2.5C12.3283 2.5 10.8333 3.995 10.8333 5.83333C10.8333 7.67167 12.3283 9.16667 14.1667 9.16667C16.005 9.16667 17.5 7.67167 17.5 5.83333C17.5 3.995 16.005 2.5 14.1667 2.5ZM14.1667 7.5C13.2475 7.5 12.5 6.7525 12.5 5.83333C12.5 4.91417 13.2475 4.16667 14.1667 4.16667C15.0858 4.16667 15.8333 4.91417 15.8333 5.83333C15.8333 6.7525 15.0858 7.5 14.1667 7.5ZM5.83333 10.8333C3.995 10.8333 2.5 12.3283 2.5 14.1667C2.5 16.005 3.995 17.5 5.83333 17.5C7.67167 17.5 9.16667 16.005 9.16667 14.1667C9.16667 12.3283 7.67167 10.8333 5.83333 10.8333ZM5.83333 15.8333C4.91417 15.8333 4.16667 15.0858 4.16667 14.1667C4.16667 13.2475 4.91417 12.5 5.83333 12.5C6.7525 12.5 7.5 13.2475 7.5 14.1667C7.5 15.0858 6.7525 15.8333 5.83333 15.8333Z" fill="#2F2B3D" />
              </svg>
              <div>
                <Typography variant="subtitle1" sx={{ color: "rgba(47, 43, 61, 0.7)", fontSize: '15px', fontWeight: 400, lineHeight: "15px", textTransform: 'uppercase', paddingBottom: "10px" }}>
                   {t('supplier_card.main_categories')}
                </Typography>
                <Typography variant="body2" sx={{ color: "#2F2B3D", fontSize: '15px', fontWeight: 400, lineHeight: "20px" }}>
                  {(supplier.main_categories || [])
                    .map((category) => t(category.name, { ns: categoriesNamespace }))
                    .join('  |  ')}
                </Typography>
              </div>
            </Grid2>
          </Grid2>

          {supplier.community_status && (
            <Typography
              sx={{ marginTop: "16px", color: "#257C6D", fontSize: "13px", fontWeight: 500, textAlign: "center" }}
            >
              {t(`community_status.${supplier.community_status}`)}
            </Typography>
          )}
          {otherTypes.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "16px", justifyContent: "center" }}>
              {otherTypes.map((type) => (
                <Box component="span" key={type} sx={otherTypesChip}>
                  {t(`also_sells.${type}`)}
                </Box>
              ))}
            </Box>
          )}
        </Box>
        <Button
          variant="contained"
          fullWidth
          sx={{ mt: 3, borderWidth: "1px", borderColor: "#257C6D", borderRadius: "8px", backgroundColor: "#257C6D", padding: "8px 12px", color: "#ffffff", fontSize: '15px', fontWeight: 500, textTransform: 'initial', boxShadow: "none", '&:hover': { boxShadow: "none" } }}
          onClick={() => {
            displaySupplierProfile(supplier);
          }}
        >
          {buttonText}
        </Button>
      </CardContent>
    </Card>
  );
};

export default SupplierCard;